import React, { createContext, useContext, useReducer, useCallback, useEffect, ReactNode, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { OrdersEditorState, OrdersAction, DeepOrder, DeclineReason, OrderFilters, FilterStatus, PaymentMethod, PaymentPaidStatus, OrderType, CancelOrderResponse } from '../types'
import { formatDateForApi, parseApiDate, requiresConfirmation, getOrderType } from '../utils'
import { useOrders } from './useOrders'
import { useOrderActions } from './useOrderActions'

// Default filters
const defaultFilters: OrderFilters = {
  status: 'all',
  paymentMethod: 'all',
  paymentPaidStatus: 'all',
  orderType: 'all',
  searchQuery: '',
  searchField: 'all',
}

// Initial state
const initialState: OrdersEditorState = {
  selectedOrderId: null,
  isDetailSheetOpen: false,
  isCancelDialogOpen: false,
  isConfirmDialogOpen: false,
  isDeclineDialogOpen: false,
  pollingEnabled: true,
  filters: defaultFilters,
}

// Reducer
function ordersReducer(
  state: OrdersEditorState,
  action: OrdersAction
): OrdersEditorState {
  switch (action.type) {
    case 'SELECT_ORDER':
      return {
        ...state,
        selectedOrderId: action.payload,
        isDetailSheetOpen: true,
      }

    case 'CLOSE_DETAIL':
      return {
        ...state,
        selectedOrderId: null,
        isDetailSheetOpen: false,
      }

    case 'OPEN_CANCEL_DIALOG':
      return {
        ...state,
        isCancelDialogOpen: true,
      }

    case 'CLOSE_CANCEL_DIALOG':
      return {
        ...state,
        isCancelDialogOpen: false,
      }

    case 'OPEN_CONFIRM_DIALOG':
      return {
        ...state,
        isConfirmDialogOpen: true,
      }

    case 'CLOSE_CONFIRM_DIALOG':
      return {
        ...state,
        isConfirmDialogOpen: false,
      }

    case 'OPEN_DECLINE_DIALOG':
      return {
        ...state,
        isDeclineDialogOpen: true,
      }

    case 'CLOSE_DECLINE_DIALOG':
      return {
        ...state,
        isDeclineDialogOpen: false,
      }

    case 'TOGGLE_POLLING':
      return {
        ...state,
        pollingEnabled: !state.pollingEnabled,
      }

    case 'SET_FILTER_STATUS':
      return {
        ...state,
        filters: { ...state.filters, status: action.payload },
      }

    case 'SET_FILTER_PAYMENT_METHOD':
      return {
        ...state,
        filters: { ...state.filters, paymentMethod: action.payload },
      }

    case 'SET_FILTER_PAYMENT_PAID_STATUS':
      return {
        ...state,
        filters: { ...state.filters, paymentPaidStatus: action.payload },
      }

    case 'SET_FILTER_ORDER_TYPE':
      return {
        ...state,
        filters: { ...state.filters, orderType: action.payload },
      }

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        filters: { ...state.filters, searchQuery: action.payload },
      }

    case 'SET_SEARCH_FIELD':
      return {
        ...state,
        filters: { ...state.filters, searchField: action.payload },
      }

    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        filters: defaultFilters,
      }

    default:
      return state
  }
}

// Context type - combines UI state with data hooks
export interface OrdersContextValue {
  // UI State
  state: OrdersEditorState
  dispatch: React.Dispatch<OrdersAction>

  // Date management
  selectedDate: Date
  setSelectedDate: (date: Date) => void
  goToToday: () => void
  goToPreviousDay: () => void
  goToNextDay: () => void

  // Order data
  orders: DeepOrder[]
  filteredOrders: DeepOrder[]
  orderCounts: Record<string, number>
  selectedOrder: DeepOrder | undefined
  pendingConfirmationOrders: DeepOrder[]
  isLoading: boolean
  isFetching: boolean
  error: Error | null

  // Filter state
  filters: OrderFilters
  activeFilterCount: number
  setFilterStatus: (status: FilterStatus) => void
  setFilterPaymentMethod: (method: PaymentMethod) => void
  setFilterPaymentPaidStatus: (status: PaymentPaidStatus) => void
  setFilterOrderType: (type: OrderType | 'all') => void
  setSearchQuery: (query: string) => void
  setSearchField: (field: 'orderId' | 'phone' | 'name' | 'all') => void
  clearAllFilters: () => void

  // Actions
  selectOrder: (orderId: number) => void
  closeDetail: () => void
  openCancelDialog: () => void
  closeCancelDialog: () => void
  openConfirmDialog: () => void
  closeConfirmDialog: () => void
  openDeclineDialog: () => void
  closeDeclineDialog: () => void
  togglePolling: () => void
  refresh: () => Promise<void>

  // Mutations
  updateOrderStatus: (orderId: number, status: string) => Promise<void>
  cancelOrder: (orderId: number, reason?: string) => Promise<CancelOrderResponse>
  confirmOrder: (orderId: number) => Promise<void>
  declineOrder: (orderId: number, reason: DeclineReason, customReason?: string) => Promise<void>
  isUpdating: boolean
  isCancelling: boolean
  isConfirming: boolean
  isDeclining: boolean
}

// Context
export const OrdersContext = createContext<OrdersContextValue | null>(null)

// Provider component
interface OrdersEditorProviderProps {
  restaurantId: number
  posEnabled?: boolean
  children: ReactNode
}

export function OrdersEditorProvider({ restaurantId, children, posEnabled = false }: OrdersEditorProviderProps) {
  const [state, dispatch] = useReducer(ordersReducer, initialState)

  // Date state - managed here for coordination with data fetching
  const [selectedDate, setSelectedDateState] = useState<Date>(() => new Date())
  const dateString = formatDateForApi(selectedDate)

  const selectedDateRef = useRef(selectedDate)
  selectedDateRef.current = selectedDate

  // Midnight rollover: an always-on tablet showing "today" must follow the
  // calendar — otherwise it silently keeps fetching yesterday's orders.
  useEffect(() => {
    let knownToday = formatDateForApi(new Date())
    const timer = setInterval(() => {
      const newToday = formatDateForApi(new Date())
      if (newToday === knownToday) return
      const wasViewingToday = formatDateForApi(selectedDateRef.current) === knownToday
      knownToday = newToday
      if (wasViewingToday) {
        setSelectedDateState(new Date())
      }
    }, 60_000)
    return () => clearInterval(timer)
  }, [])

  // Allow deep-linking from notifications: /orders?orderId=N&date=YYYY-MM-DD
  // Sets the day to the order's date and pre-selects the order in the panel,
  // then strips the params so back-nav and refreshes behave normally.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const dateParam = searchParams.get('date')
    const orderIdParam = searchParams.get('orderId')
    if (!dateParam && !orderIdParam) return
    if (dateParam) {
      const parsed = parseApiDate(dateParam)
      if (!isNaN(parsed.getTime())) {
        setSelectedDateState(parsed)
      }
    }
    if (orderIdParam) {
      const id = Number(orderIdParam)
      if (Number.isFinite(id)) {
        dispatch({ type: 'SELECT_ORDER', payload: id })
      }
    }
    const next = new URLSearchParams(searchParams)
    next.delete('date')
    next.delete('orderId')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Data hooks
  const ordersData = useOrders(restaurantId, dateString, posEnabled)
  const orderActions = useOrderActions(restaurantId, dateString)
  const {
    orders,
    orderCounts,
    isLoading,
    isFetching,
    error,
    refresh,
    startPolling,
    stopPolling,
  } = ordersData
  const filters = state.filters

  // Start/stop polling based on pollingEnabled
  useEffect(() => {
    if (state.pollingEnabled) {
      startPolling(30000) // 30 second intervals
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [state.pollingEnabled, startPolling, stopPolling])

  // Get selected order from data
  const selectedOrder = useMemo(() => {
    if (state.selectedOrderId === null) return undefined
    return orders.find((order) => order.id === state.selectedOrderId)
  }, [orders, state.selectedOrderId])

  // Get orders pending confirmation
  const pendingConfirmationOrders = useMemo(() => {
    return orders.filter(requiresConfirmation)
  }, [orders])

  // Filter orders based on current filters
  const filteredOrders = useMemo(() => {
    let result = orders

    // Filter by status
    if (filters.status !== 'all') {
      if (filters.status === 'cancelled') {
        result = result.filter((order) => order.cancelStatus === 'cancelled')
      } else {
        result = result.filter(
          (order) =>
            order.status.toLowerCase() === filters.status &&
            order.cancelStatus !== 'cancelled'
        )
      }
    }

    // Filter by payment method
    if (filters.paymentMethod !== 'all') {
      result = result.filter((order) => {
        const ps = order.paymentStatus.toLowerCase()
        if (order.posPaymentUnavailable) return false
        if (order.posPayment) return filters.paymentMethod === (order.posPayment.method === 'cash' ? 'cash' : 'card')
        switch (filters.paymentMethod) {
          case 'cash':
            return ps === 'cash'
          case 'card':
            return ps === 'cc_success'
          case 'paypal':
            return ps === 'paypal_success' || ps === 'paypalme_new'
          case 'stripe':
            return ps === 'stripe_success'
          default:
            return true
        }
      })
    }

    // Filter by payment paid status
    if (filters.paymentPaidStatus !== 'all') {
      result = result.filter((order) => {
        const ps = order.paymentStatus.toLowerCase()
        if (order.posPaymentUnavailable) return false
        const isPaid =
          Boolean(order.posPayment) ||
          ps === 'paypal_success' ||
          ps === 'cc_success' ||
          ps === 'stripe_success' ||
          ps === 'paypalme_new'
        return filters.paymentPaidStatus === 'paid' ? isPaid : !isPaid
      })
    }

    // Filter by order type
    if (filters.orderType !== 'all') {
      result = result.filter((order) => getOrderType(order) === filters.orderType)
    }

    // Apply search query
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim()
      result = result.filter((order) => {
        switch (filters.searchField) {
          case 'orderId':
            return order.id.toString().includes(query)
          case 'phone':
            return order.telephone.toLowerCase().includes(query)
          case 'name':
            return order.deliveryAddress?.name.toLowerCase().includes(query) ?? false
          case 'all':
          default:
            return (
              order.id.toString().includes(query) ||
              order.telephone.toLowerCase().includes(query) ||
              (order.deliveryAddress?.name.toLowerCase().includes(query) ?? false)
            )
        }
      })
    }

    return result
  }, [orders, filters])

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.status !== 'all') count++
    if (filters.paymentMethod !== 'all') count++
    if (filters.paymentPaidStatus !== 'all') count++
    if (filters.orderType !== 'all') count++
    if (filters.searchQuery.trim()) count++
    return count
  }, [filters])

  // Filter actions
  const setFilterStatus = useCallback((status: FilterStatus) => {
    dispatch({ type: 'SET_FILTER_STATUS', payload: status })
  }, [])

  const setFilterPaymentMethod = useCallback((method: PaymentMethod) => {
    dispatch({ type: 'SET_FILTER_PAYMENT_METHOD', payload: method })
  }, [])

  const setFilterPaymentPaidStatus = useCallback((status: PaymentPaidStatus) => {
    dispatch({ type: 'SET_FILTER_PAYMENT_PAID_STATUS', payload: status })
  }, [])

  const setFilterOrderType = useCallback((type: OrderType | 'all') => {
    dispatch({ type: 'SET_FILTER_ORDER_TYPE', payload: type })
  }, [])

  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query })
  }, [])

  const setSearchField = useCallback((field: 'orderId' | 'phone' | 'name' | 'all') => {
    dispatch({ type: 'SET_SEARCH_FIELD', payload: field })
  }, [])

  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
  }, [])

  // Date navigation
  const setSelectedDate = useCallback((date: Date) => {
    setSelectedDateState(date)
    // Clear selection when changing date
    dispatch({ type: 'CLOSE_DETAIL' })
  }, [])

  const goToToday = useCallback(() => {
    setSelectedDate(new Date())
  }, [setSelectedDate])

  const goToPreviousDay = useCallback(() => {
    const prev = new Date(selectedDate)
    prev.setDate(prev.getDate() - 1)
    setSelectedDate(prev)
  }, [selectedDate, setSelectedDate])

  const goToNextDay = useCallback(() => {
    const next = new Date(selectedDate)
    next.setDate(next.getDate() + 1)
    setSelectedDate(next)
  }, [selectedDate, setSelectedDate])

  // UI Actions
  const selectOrder = useCallback((orderId: number) => {
    dispatch({ type: 'SELECT_ORDER', payload: orderId })
  }, [])

  const closeDetail = useCallback(() => {
    dispatch({ type: 'CLOSE_DETAIL' })
  }, [])

  const openCancelDialog = useCallback(() => {
    dispatch({ type: 'OPEN_CANCEL_DIALOG' })
  }, [])

  const closeCancelDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_CANCEL_DIALOG' })
  }, [])

  const openConfirmDialog = useCallback(() => {
    dispatch({ type: 'OPEN_CONFIRM_DIALOG' })
  }, [])

  const closeConfirmDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_CONFIRM_DIALOG' })
  }, [])

  const openDeclineDialog = useCallback(() => {
    dispatch({ type: 'OPEN_DECLINE_DIALOG' })
  }, [])

  const closeDeclineDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_DECLINE_DIALOG' })
  }, [])

  const togglePolling = useCallback(() => {
    dispatch({ type: 'TOGGLE_POLLING' })
  }, [])

  // Mutation wrappers with error handling
  const updateOrderStatus = useCallback(async (orderId: number, status: string) => {
    await orderActions.updateStatus.mutateAsync({ orderId, orderStatus: status })
  }, [orderActions.updateStatus])

  const cancelOrderAction = useCallback(async (orderId: number, reason?: string): Promise<CancelOrderResponse> => {
    const response = await orderActions.cancelOrder.mutateAsync({ orderId, cancelReason: reason })
    closeCancelDialog()
    return response
  }, [orderActions.cancelOrder, closeCancelDialog])

  const confirmOrderAction = useCallback(async (orderId: number) => {
    await orderActions.confirmOrder.mutateAsync({ orderId })
    closeConfirmDialog()
  }, [orderActions.confirmOrder, closeConfirmDialog])

  const declineOrderAction = useCallback(async (orderId: number, reason: DeclineReason, customReason?: string) => {
    await orderActions.declineOrder.mutateAsync({ orderId, reason, customReason })
    closeDeclineDialog()
  }, [orderActions.declineOrder, closeDeclineDialog])

  // Memoized so consumers only re-render when something actually changed,
  // not on every provider render triggered by polling/refetch cycles.
  const value: OrdersContextValue = useMemo(() => ({
    // UI State
    state,
    dispatch,

    // Date management
    selectedDate,
    setSelectedDate,
    goToToday,
    goToPreviousDay,
    goToNextDay,

    // Order data
    orders,
    filteredOrders,
    orderCounts,
    selectedOrder,
    pendingConfirmationOrders,
    isLoading,
    isFetching,
    error,

    // Filter state
    filters,
    activeFilterCount,
    setFilterStatus,
    setFilterPaymentMethod,
    setFilterPaymentPaidStatus,
    setFilterOrderType,
    setSearchQuery,
    setSearchField,
    clearAllFilters,

    // UI Actions
    selectOrder,
    closeDetail,
    openCancelDialog,
    closeCancelDialog,
    openConfirmDialog,
    closeConfirmDialog,
    openDeclineDialog,
    closeDeclineDialog,
    togglePolling,
    refresh,

    // Mutations
    updateOrderStatus,
    cancelOrder: cancelOrderAction,
    confirmOrder: confirmOrderAction,
    declineOrder: declineOrderAction,
    isUpdating: orderActions.isUpdating,
    isCancelling: orderActions.isCancelling,
    isConfirming: orderActions.isConfirming,
    isDeclining: orderActions.isDeclining,
  }), [
    state, selectedDate, setSelectedDate, goToToday, goToPreviousDay, goToNextDay,
    orders, filteredOrders, orderCounts, selectedOrder, pendingConfirmationOrders,
    isLoading, isFetching, error,
    filters, activeFilterCount, setFilterStatus, setFilterPaymentMethod,
    setFilterPaymentPaidStatus, setFilterOrderType, setSearchQuery, setSearchField, clearAllFilters,
    selectOrder, closeDetail, openCancelDialog, closeCancelDialog, openConfirmDialog,
    closeConfirmDialog, openDeclineDialog, closeDeclineDialog, togglePolling, refresh,
    updateOrderStatus, cancelOrderAction, confirmOrderAction, declineOrderAction,
    orderActions.isUpdating, orderActions.isCancelling, orderActions.isConfirming, orderActions.isDeclining,
  ])

  return (
    <OrdersContext.Provider value={value}>
      {children}
    </OrdersContext.Provider>
  )
}

// Hook to use context
export function useOrdersEditor(): OrdersContextValue {
  const context = useContext(OrdersContext)
  if (!context) {
    throw new Error('useOrdersEditor must be used within OrdersEditorProvider')
  }
  return context
}
